// The console's one analytics question and PostHog's lifecycle (plan step 2.4; D58): asked once
// after sign-in, kept on the profile, applied on every page. Builds without a PostHog key show
// nothing and load nothing.
import { useLocation } from '@tanstack/react-router'
import { useEffect, useId } from 'react'
import { useUpdateProfile } from '../api/queries'
import type { Me } from '../api/types'
import { useI18n } from '../i18n'
import { consoleAnalytics } from '../lib/analytics'

/** The privacy policy's cookie section, in the console's language. */
export const privacyUrl = (locale: string) =>
  `https://apsio.io/${locale === 'pt-BR' ? 'pt-br/' : ''}privacy/#cookies`

export function Analytics({ me }: { me: Me | undefined }) {
  const analytics = consoleAnalytics()
  const location = useLocation()
  const userId = me?.user.id
  const consent = me?.user.analytics_consent ?? null
  useEffect(() => {
    // Not signed in, or not answered yet: cookieless counts only (D58 addendum).
    if (analytics) void analytics.apply(consent ?? 'pending', userId)
  }, [analytics, userId, consent])
  useEffect(() => {
    analytics?.page(location.pathname, location.search as Record<string, unknown>)
  }, [analytics, location.pathname, location.search])
  if (!analytics || !me || consent) return null
  return <ConsentPrompt />
}

/** Accept and Reject side by side, the same size and weight: no default, no dark pattern. */
export function ConsentPrompt() {
  const { t, locale } = useI18n()
  const update = useUpdateProfile()
  const title = useId()
  const answer = (analytics_consent: 'granted' | 'denied') => update.mutate({ analytics_consent })
  return (
    <section className="consent" aria-labelledby={title}>
      <h2 id={title}>{t('consent.title')}</h2>
      <p>
        {t('consent.body')} <a href={privacyUrl(locale)}>{t('consent.privacy')}</a>
      </p>
      <div className="consent-actions">
        <button
          type="button"
          className="btn"
          disabled={update.isPending}
          onClick={() => answer('granted')}
        >
          {t('consent.accept')}
        </button>
        <button
          type="button"
          className="btn"
          disabled={update.isPending}
          onClick={() => answer('denied')}
        >
          {t('consent.reject')}
        </button>
      </div>
      {update.isError ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
    </section>
  )
}
